import React, { useEffect, useRef, useState } from "react";
import "./MapPicker.css";

export default function MapPicker({ onLocationSelect, initialLocation }) {
    const mapDivRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const geocoderRef = useRef(null);
    const searchInputRef = useRef(null);
    const autocompleteRef = useRef(null);

    const [selectedAddress, setSelectedAddress] = useState("");
    const [selectedState, setSelectedState] = useState("");

    const parseStateFromComponents = (components = []) => {
        const st = components.find(c => c.types?.includes("administrative_area_level_1"));
        return st?.short_name || "";
    };

    const ensureMarker = () => {
        if (!markerRef.current && mapRef.current) {
            markerRef.current = new window.google.maps.Marker({
                map: mapRef.current,
                draggable: true,
            });
            markerRef.current.addListener("dragend", () => {
                const pos = markerRef.current.getPosition();
                reverseGeocode(pos);
            });
        }
        return markerRef.current;
    };

    const setMarkerAndNotify = (latLng, address, components) => {
        const marker = ensureMarker();
        marker.setPosition(latLng);
        mapRef.current.panTo(latLng);
        const state = parseStateFromComponents(components);
        setSelectedAddress(address || "");
        setSelectedState(state);
        onLocationSelect?.({
            lat: latLng.lat(),
            lng: latLng.lng(),
            address: address || "",
            state,
        });
    };

    const reverseGeocode = (latLng) => {
        if (!geocoderRef.current) return;
        geocoderRef.current.geocode({ location: latLng }, (results, status) => {
            if (status === "OK" && results?.[0]) {
                setMarkerAndNotify(latLng, results[0].formatted_address, results[0].address_components);
            }
        });
    };

    const initMap = () => {
        if (mapRef.current) return; // already initialized

        const defaultCenter = { lat: 40.7128, lng: -74.006 }; // NYC
        mapRef.current = new window.google.maps.Map(mapDivRef.current, {
            center: defaultCenter,
            zoom: 12,
            streetViewControl: false,
            mapTypeControl: false,
        });

        geocoderRef.current = new window.google.maps.Geocoder();

        // Map click => place/move marker + reverse geocode
        mapRef.current.addListener("click", (e) => {
            const latLng = e.latLng;
            setMarkerAndNotify(latLng, null, []);
            reverseGeocode(latLng);
        });

        // Autocomplete
        if (window.google.maps.places && searchInputRef.current) {
            autocompleteRef.current = new window.google.maps.places.Autocomplete(searchInputRef.current, {
                fields: ["geometry", "formatted_address", "address_components"],
                types: ["geocode"],
            });
            autocompleteRef.current.bindTo("bounds", mapRef.current);

            autocompleteRef.current.addListener("place_changed", () => {
                const place = autocompleteRef.current.getPlace();
                if (!place || !place.geometry) return;
                const loc = place.geometry.location;
                setMarkerAndNotify(loc, place.formatted_address, place.address_components);
            });
        }
    };

    const ensureScript = () => {
        if (window.google?.maps?.places) {
            initMap();
            return;
        }
        const existing = document.getElementById("google-maps-script");
        if (existing) {
            existing.addEventListener("load", initMap, { once: true });
            return;
        }
        const script = document.createElement("script");
        script.id = "google-maps-script";
        script.src = "https://maps.googleapis.com/maps/api/js?key=AIzaSyD8sLxJhds79zvy45b2EbARfrpTZl0aQko&libraries=places";
        script.async = true;
        script.defer = true;
        script.addEventListener("load", initMap, { once: true });
        document.body.appendChild(script);
    };

    useEffect(() => {
        ensureScript();
        // no deps: run once, keep instances in refs
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Set initial location if provided (string address)
    useEffect(() => {
        if (initialLocation && selectedAddress === "") {
            let cancelled = false;
            const tryGeocode = () => {
                if (window.google?.maps && mapRef.current && geocoderRef.current) {
                    geocoderRef.current.geocode({ address: initialLocation }, (results, status) => {
                        if (cancelled) return;
                        if (status === "OK" && results?.[0]) {
                            const latLng = results[0].geometry.location;
                            setMarkerAndNotify(latLng, results[0].formatted_address, results[0].address_components);
                            mapRef.current.setZoom(15);
                        } else {
                            setSelectedAddress(initialLocation);
                            setSelectedState("");
                        }
                    });
                } else {
                    setTimeout(tryGeocode, 200);
                }
            };
            tryGeocode();
            return () => { cancelled = true; };
        }
    }, [initialLocation]);

    const clearSelection = () => {
        setSelectedAddress("");
        setSelectedState("");
        if (markerRef.current) {
            markerRef.current.setMap(null);
            markerRef.current = null;
        }
        if (searchInputRef.current) {
            searchInputRef.current.value = "";
        }
        onLocationSelect?.({ lat: null, lng: null, address: "", state: "" });
        if (mapRef.current) mapRef.current.panTo({ lat: 40.7128, lng: -74.006 });
    };

    const handleManualInput = (e) => {
        const inputValue = e.target.value;
        setSelectedAddress(inputValue);
        setSelectedState("");
        onLocationSelect?.({
            lat: null,
            lng: null,
            address: inputValue,
            state: "",
        });
    };

    return (
        <div>
            <div className="d-flex justify-content-between">
                <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search location..."
                    className="mappicker-search"
                    style={{ width: selectedAddress ? "90%" : "100%" }}
                    value={selectedAddress}
                    onChange={handleManualInput}
                />
                {(selectedAddress || selectedState) &&
                    <button className="mappicker-clear" onClick={clearSelection}><i className="fa fa-times" /></button>
                }
            </div>
            <div ref={mapDivRef} className="mappicker-map" />
        </div>
    );
}
